import React, { useState, useEffect, useCallback } from "react";
import { dbPayload } from "../../../../auth/registered/SEC_00_DB_Payload"; 
import { GoldenSourceHelper } from "../../../../app/Switch_Core_React_Standards";
import GenericManagerView from "./SEC_03_GenericManagerView"; 

export default function SEC_03_GenericManagerController({ config, allConfigs }) {
    const [data, setData] = useState([]);
    const [schema, setSchema] = useState([]); 
    const [loading, setLoading] = useState(false);
    
    // 🎯 SUB-LIST STATE
    const [subListData, setSubListData] = useState([]);
    const [masterPool, setMasterPool] = useState([]);

    /**
     * 🛰️ PRIMARY FETCH
     * Retrieves the main list and schema for the active pillar.
     */
    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const siteKey = process.env.REACT_APP_SITE_KEY;
            // Note: Standardized to POST for metadata discovery
            const rawResponse = await dbPayload(config.endpoints.fetch, "POST", siteKey, {});
            
            if (rawResponse && rawResponse.metadata) {
                setSchema(rawResponse.metadata.columns);
            }

            const res = GoldenSourceHelper.normalize(rawResponse);
            if (res.success) {
                setData(res.data || []); 
            }
        } catch (err) {
            console.error(`❌ ${config.title} Fetch Failed:`, err.message);
        } finally {
            setLoading(false);
        }
    }, [config]);

    useEffect(() => { fetchList(); }, [fetchList]);

    /**
     * 🛰️ SUB-LIST FETCH (RELATIONSHIPS)
     * Called by the "SHOW" toggle in the view.
     */
    const handleShowSubList = async (parentRow) => {
        const relation = config.relationship;
        if (!relation) return;

        try {
            const siteKey = process.env.REACT_APP_SITE_KEY;
            
            // 1. Fetch Existing Links for this specific row
            const linkPath = `${relation.endpoints.fetchLinks}/${parentRow.id}`;
            const linkResponse = await dbPayload(linkPath, "POST", siteKey, {});
            const normalizedLinks = GoldenSourceHelper.normalize(linkResponse);
            setSubListData(normalizedLinks.data || []);

            // 2. Fetch Master Pool from the sibling config (e.g., Resources)
            const childPillar = allConfigs[relation.childEntity];
            if (childPillar) {
                const poolResponse = await dbPayload(childPillar.endpoints.fetch, "POST", siteKey, {});
                const normalizedPool = GoldenSourceHelper.normalize(poolResponse);
                setMasterPool(normalizedPool.data || []);
            }
        } catch (err) {
            console.error("❌ Sub-list Fetch Error:", err.message);
        }
    };

    /**
     * 🛰️ UPDATE LOGIC
     */
    const handleUpdate = async (idInList, updatedFields) => {
        try {
            const siteKey = process.env.REACT_APP_SITE_KEY;
            const rawResponse = await dbPayload(config.endpoints.update, "POST", siteKey, updatedFields);
            
            const res = GoldenSourceHelper.normalize(rawResponse);
            if (res.success) {
                setData(prev => prev.map((item, idx) => (idx === idInList ? { ...item, ...updatedFields } : item)));
            } else {
                alert("Update failed: " + (res.message || "Unknown error"));
            }
        } catch (err) {
            console.error("❌ Generic Update Error:", err.message);
        }
    };

    /**
     * 🛰️ ADD LOGIC
     */
    const handleAdd = async (newRow) => {
        try {
            const siteKey = process.env.REACT_APP_SITE_KEY;
            const rawResponse = await dbPayload(config.endpoints.add, "POST", siteKey, newRow);
            const res = GoldenSourceHelper.normalize(rawResponse);
            if (res.success) fetchList(); 
        } catch (err) {
            console.error("❌ Generic Add Error:", err.message);
        }
    };

    /**
     * 🛰️ LINK CREATION LOGIC
     * Handles creating the unique relationship between parent and child.
     */
    const handleAddLink = async (parentId, childId) => {
        const relation = config.relationship;
        try {
            const siteKey = process.env.REACT_APP_SITE_KEY;
            const payload = { parentId, childId };
            const rawResponse = await dbPayload(relation.endpoints.addLink, "POST", siteKey, payload);
            
            const res = GoldenSourceHelper.normalize(rawResponse);
            if (res.success) {
                // Refresh sub-list to show the new link
                handleShowSubList({ id: parentId });
            }
        } catch (err) {
            console.error("❌ Link Creation Error:", err.message);
        }
    };

    return (
        <GenericManagerView 
            config={config} 
            schema={schema} 
            data={data} 
            loading={loading} 
            onUpdate={handleUpdate}
            onAdd={handleAdd}
            // Sub-list Props
            subListData={subListData}
            masterPool={masterPool}
            onShowSubList={handleShowSubList}
            onAddLink={handleAddLink}
        />
    );
}